import React, { useState } from 'react';
import { Plus, UserPlus, CreditCard, Calendar, FileText, MessageCircle, Settings, RotateCcw } from 'lucide-react';
import { PermissionGate } from '../Common/PermissionGate';
import { PERMISSIONS } from '../../utils/permissions';
import { useAuth } from '../../contexts/AuthContext';
import { useGlobalToast } from '../../contexts/ToastContext';
import { api } from '../../services/api';

interface QuickActionsProps {
  onAction: (action: string) => void;
}

export const QuickActions: React.FC<QuickActionsProps> = ({ onAction }) => {
  const { user, logout } = useAuth();
  const { success, error } = useGlobalToast();
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const actions = [
    { id: 'members', label: 'Manage Members', icon: UserPlus, color: 'blue', permission: PERMISSIONS.VIEW_MEMBERS },
    { id: 'contributions', label: 'Record Payment', icon: CreditCard, color: 'green', permission: PERMISSIONS.MANAGE_CONTRIBUTIONS },
    { id: 'events', label: 'Create Event', icon: Calendar, color: 'purple', permission: PERMISSIONS.MANAGE_EVENTS },
    { id: 'sms', label: 'Send SMS', icon: MessageCircle, color: 'orange', permission: PERMISSIONS.SEND_SMS },
    { id: 'settings', label: 'System Settings', icon: Settings, color: 'red', permission: PERMISSIONS.APPROVE_FINANCES },
    { id: 'reports', label: 'Generate Report', icon: FileText, color: 'gray', permission: PERMISSIONS.VIEW_REPORTS }
  ];
  
  const handleFreshStart = async () => {
    setLoading(true);
    try {
      await api.post('/system/fresh-start');
      success('System reset complete. Only developer account preserved.');
      logout();
    } catch (error) {
      error('Fresh start failed: ' + (error as any).message);
    } finally {
      setLoading(false);
      setShowConfirm(false);
    }
  };

  const colorClasses = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    green: 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 text-green-600 dark:text-green-400 border-green-200 dark:border-green-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    orange: 'bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-200 dark:border-orange-800',
    red: 'bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800',
    gray: 'bg-gray-50 dark:bg-gray-800/20 hover:bg-gray-100 dark:hover:bg-gray-800/30 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
  };

  return (
    <div className="bg-white dark:bg-dark-bg-secondary rounded-xl shadow-sm border dark:border-dark-border-primary p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4 flex items-center">
        <Plus className="h-5 w-5 mr-2" />
        Quick Actions
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
        {actions.map((action) => (
          <PermissionGate key={action.id} permission={action.permission}>
            <button
              onClick={() => onAction(action.id)}
              className={`w-full flex items-center justify-center sm:justify-start space-x-3 p-3 rounded-lg border transition-colors ${
                colorClasses[action.color as keyof typeof colorClasses]
              }`}
            >
              <action.icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium hidden sm:block lg:block">{action.label}</span>
            </button>
          </PermissionGate>
        ))}
        
        {user?.role === 'developer' && (
          <>
            <button
              onClick={() => onAction('developer-panel')}
              className="w-full flex items-center justify-center sm:justify-start space-x-3 p-3 rounded-lg border transition-colors bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800"
            >
              <Settings className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium hidden sm:block lg:block">Developer Panel</span>
            </button>
            
            <button
              onClick={() => setShowConfirm(true)}
              className="w-full flex items-center justify-center sm:justify-start space-x-3 p-3 rounded-lg border transition-colors bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800"
            >
              <RotateCcw className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium hidden sm:block lg:block">Fresh Start</span>
            </button>
          </>
        )}
      </div>
      
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
              Confirm Fresh Start
            </h3>
            <p className="text-gray-600 dark:text-dark-text-secondary mb-6">
              This will permanently delete ALL data (members, contributions, events, etc.) and reset the system to a clean state. Only the developer account will be preserved.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 py-2 px-4 rounded-lg"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleFreshStart}
                disabled={loading}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-lg disabled:opacity-50"
              >
                {loading ? 'Resetting...' : 'Reset System'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};